import { createFileRoute, Link } from "@tanstack/react-router";
import { Instagram, Facebook, Phone, Mail, MapPin, Award, ArrowRight } from "lucide-react";
import { buildHead, PAGE_SEO, pickLang, type SeoLang } from "@/lib/seo";
import { useI18n } from "@/lib/i18n";
import { fetchAgentProfile } from "@/lib/publicData";
import { TimelineSection } from "@/components/public/TimelineSection";

const COPY: Record<SeoLang, Record<string, string>> = {
  es: {
    role: "Asesora inmobiliaria",
    bio: "Asesora inmobiliaria con foco en compra, venta y alquiler de propiedades residenciales en Asunción y Central. Trato cercano, transparencia y acompañamiento en cada paso.",
    whatsapp: "Contactar por WhatsApp",
    sell: "Vendé tu propiedad",
    years: "años",
    yearsDesc: "de experiencia asesorando en Paraguay.",
    zones: "Zonas de cobertura",
    contact: "Contacto directo",
    how: "Cómo trabajamos",
    buy: "Compra",
    buyD: "Búsqueda personalizada según tus prioridades y presupuesto.",
    sale: "Venta",
    saleD: "Tasación, fotos profesionales y publicación destacada.",
    rent: "Alquiler",
    rentD: "Filtrado de inquilinos y gestión contractual completa.",
    social: "Seguime en redes",
    socialD: "Publicaciones nuevas, tips inmobiliarios y oportunidades destacadas.",
  },
  en: {
    role: "Real estate advisor",
    bio: "Real estate advisor focused on buying, selling and renting residential property in Asunción and the Central department of Paraguay. Personal service, transparency and guidance at every step.",
    whatsapp: "Contact on WhatsApp",
    sell: "Sell your property",
    years: "years",
    yearsDesc: "advising clients in Paraguay.",
    zones: "Areas covered",
    contact: "Direct contact",
    how: "How we work",
    buy: "Buying",
    buyD: "A tailored search based on your priorities and budget.",
    sale: "Selling",
    saleD: "Valuation, professional photography and featured listings.",
    rent: "Renting",
    rentD: "Tenant screening and full contract management.",
    social: "Follow me",
    socialD: "New listings, real estate tips and featured opportunities.",
  },
  pt: {
    role: "Consultora imobiliária",
    bio: "Consultora imobiliária focada em compra, venda e locação de imóveis residenciais em Assunção e no departamento Central do Paraguai. Atendimento próximo, transparência e acompanhamento em cada etapa.",
    whatsapp: "Falar pelo WhatsApp",
    sell: "Venda seu imóvel",
    years: "anos",
    yearsDesc: "de experiência assessorando no Paraguai.",
    zones: "Regiões atendidas",
    contact: "Contato direto",
    how: "Como trabalhamos",
    buy: "Compra",
    buyD: "Busca personalizada conforme suas prioridades e orçamento.",
    sale: "Venda",
    saleD: "Avaliação, fotos profissionais e anúncio em destaque.",
    rent: "Locação",
    rentD: "Seleção de inquilinos e gestão contratual completa.",
    social: "Me siga nas redes",
    socialD: "Novos imóveis, dicas imobiliárias e oportunidades em destaque.",
  },
};

export const Route = createFileRoute("/_public/agente")({
  loader: () => fetchAgentProfile(),
  head: ({ match }) => {
    const lang = pickLang(match.search);
    const copy = PAGE_SEO["agente"]![lang];
    return buildHead({ lang, path: "/agente", ...copy });
  },
  component: AgentPage,
});

function AgentPage() {
  const agent = Route.useLoaderData();
  const { lang } = useI18n();
  const c = COPY[lang];

  const name = agent?.full_name ?? "Dahiana Marín";
  const bio = (lang === "es" ? agent?.bio : null) ?? c["bio"]!;
  const years = 2;
  const zones = ["Paraguay", "Asunción", "Gran Asunción"];
  const phone = agent?.phone_whatsapp ?? "+595986733480";
  const email = agent?.email ?? "ventas@dahianamarin.com";
  const photo = agent?.photo_url;
  const wa = phone.replace(/[^\d]/g, "");

  return (
    <>
      {/* Hero */}
      <section className="relative overflow-hidden bg-primary text-primary-foreground">
        <div className="absolute inset-0 opacity-[0.06] [background-image:radial-gradient(circle_at_1px_1px,white_1px,transparent_0)] [background-size:24px_24px]" />
        <div className="relative mx-auto grid max-w-6xl gap-10 px-4 py-20 md:grid-cols-[1.2fr_1fr] md:items-center md:px-6">
          <div>
            <p className="mb-4 inline-flex items-center gap-2 rounded-full border border-accent/40 bg-white/5 px-3 py-1 text-xs font-medium uppercase tracking-wider text-accent">
              <span className="h-1.5 w-1.5 rounded-full bg-accent" /> {c["role"]}
            </p>
            <h1 className="font-serif text-4xl leading-tight md:text-6xl">{name}</h1>
            <p className="mt-5 max-w-xl text-base text-primary-foreground/85 md:text-lg">{bio}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href={`https://wa.me/${wa}`}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="inline-flex items-center gap-2 rounded-lg bg-accent px-5 py-3 text-sm font-semibold text-accent-foreground shadow-soft transition hover:brightness-95"
              >
                <Phone className="h-4 w-4" /> {c["whatsapp"]}
              </a>
              <Link
                to="/vende-tu-propiedad"
                className="inline-flex items-center gap-2 rounded-lg border border-primary-foreground/25 px-5 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-white/5"
              >
                {c["sell"]} <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
          <div className="mx-auto w-full max-w-sm">
            <div className="aspect-[4/5] overflow-hidden rounded-2xl bg-white/10 shadow-card">
              {photo ? (
                <img src={photo} alt={name} className="h-full w-full object-cover" />
              ) : (
                <div className="grid h-full w-full place-items-center font-serif text-8xl text-accent/70">
                  {name.charAt(0)}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="mx-auto grid max-w-6xl gap-4 px-4 py-14 md:grid-cols-3 md:px-6">
        {years !== null && (
          <div className="rounded-xl border border-border bg-card p-6">
            <Award className="h-6 w-6 text-accent" />
            <div className="mt-3 text-3xl font-semibold text-foreground">
              {years}+ {c["years"]}
            </div>
            <p className="mt-1 text-sm text-muted-foreground">{c["yearsDesc"]}</p>
          </div>
        )}
        <div className="rounded-xl border border-border bg-card p-6">
          <MapPin className="h-6 w-6 text-accent" />
          <div className="mt-3 text-lg font-semibold text-foreground">{c["zones"]}</div>
          <p className="mt-1 text-sm text-muted-foreground">{zones.join(" · ")}</p>
        </div>
        <div className="rounded-xl border border-border bg-card p-6">
          <Mail className="h-6 w-6 text-accent" />
          <div className="mt-3 text-lg font-semibold text-foreground">{c["contact"]}</div>
          <p className="mt-1 text-sm text-muted-foreground break-all">{email}</p>
        </div>
      </section>

      {/* Trayectoria */}
      <TimelineSection />


      {/* Servicios */}
      <section className="mx-auto max-w-6xl px-4 pb-16 md:px-6">
        <h2 className="text-2xl font-semibold text-foreground md:text-3xl">{c["how"]}</h2>
        <div className="mt-8 grid gap-5 md:grid-cols-3">
          {[
            { t: c["buy"]!, d: c["buyD"]! },
            { t: c["sale"]!, d: c["saleD"]! },
            { t: c["rent"]!, d: c["rentD"]! },
          ].map((s) => (
            <div key={s.t} className="rounded-xl border border-border bg-card p-6">
              <div className="font-serif text-xl text-foreground">{s.t}</div>
              <p className="mt-2 text-sm text-muted-foreground">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Redes */}
      <section className="mx-auto max-w-6xl px-4 pb-24 md:px-6">
        <div className="flex flex-wrap items-center gap-4 rounded-2xl bg-secondary p-8">
          <div className="flex-1 min-w-[220px]">
            <h3 className="text-xl font-semibold text-foreground">{c["social"]}</h3>
            <p className="mt-1 text-sm text-muted-foreground">{c["socialD"]}</p>
          </div>
          <div className="flex gap-3">
            {agent?.instagram_url && (
              <a
                href={agent.instagram_url}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-primary text-primary-foreground"
                aria-label="Instagram"
              >
                <Instagram className="h-5 w-5" />
              </a>
            )}
            {agent?.facebook_url && (
              <a
                href={agent.facebook_url}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-primary text-primary-foreground"
                aria-label="Facebook"
              >
                <Facebook className="h-5 w-5" />
              </a>
            )}
            <a
              href={`https://wa.me/${wa}`}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="inline-flex h-11 items-center gap-2 rounded-lg bg-accent px-4 text-sm font-semibold text-accent-foreground"
            >
              <Phone className="h-4 w-4" /> WhatsApp
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
